<template>
  <vee-form v-slot="{ validate }" as="" :initial-errors="initialErrors">
    <form
      ref="formRef"
      class="standard-form"
      method="post"
      action="/opt-out"
      novalidate
      @submit.prevent="handleSubmit(validate)"
    >
      <text-input
        v-model="formData.email"
        input-id="email"
        type="email"
        label="Email"
        placeholder="Email Address"
        rules="required|email|max:100"
        :validate-aggressive="false"
        maxlength="100"
        autocomplete="email"
      />

      <form-fieldset-slot
        legend="Unsubscribe From"
        legend-classes="heading -small demi"
        input-name=""
        :show-error-msg="false"
      >
        <checkbox-input
          v-model="formData.all"
          input-id="all"
          name="all"
          label="All Emails"
          :checked-value="1"
          :unchecked-value="0"
        />
      </form-fieldset-slot>

      <form-fieldset-slot
        legend="Or Individual Lists"
        legend-classes="heading -small demi"
        input-name="typeIds[]"
      >
        <template v-for="emailConsent in props.emailConsents" :key="emailConsent.typeId">
          <checkbox-input
            v-model="formData.typeIds"
            :input-id="`consent_${emailConsent.typeId}`"
            name="typeIds[]"
            :checked-value="emailConsent.typeId"
            unchecked-value=""
          >
            <template #label>
              <span class="-flex -column">
                <span>{{ emailConsent.typeName }}</span>
              </span>
            </template>
          </checkbox-input>
        </template>
      </form-fieldset-slot>

      <div class="form-actions -flex -center">
        <div class="form-action">
          <csrf-input />
          <submit-button
            :submitting="submitting"
            label="Update Preferences"
            submitting-label="Processing…"
            classes="-main -no-case"
            @submit-action="handleSubmit(validate)"
          />
        </div>
      </div>
    </form>
  </vee-form>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { Form as VeeForm, type PrivateFormContext } from 'vee-validate'
import { errorBagToInitialErrors } from '@/utils/forms/validation-rules'
import SubmitButton from '@/components/buttons/submit-button.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import TextInput from '@/components/forms/text-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import type { EmailConsent } from '@/types/user-auth-types'
import FormFieldsetSlot from '@/components/forms/form-fieldset-slot.vue'
import type { ErrorBag } from '@/types/global-types'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    email: string
    emailConsents: EmailConsent[]
    errorBag?: ErrorBag | null
  }>(),
  {
    errorBag: null,
  },
)

/** STATE **/
const formData = ref<{
  email: string
  all: boolean
  typeIds: number[]
}>({
  email: props.email || '',
  all: false,
  typeIds: [],
})
const submitting = ref(false)
const formRef = ref<HTMLElement | null>(null)

/** NON REACTIVE **/
const initialErrors = errorBagToInitialErrors(props.errorBag)

/** METHODS **/
async function handleSubmit(validate: PrivateFormContext['validate']) {
  // no double submit
  if (submitting.value) {
    return
  }

  // validation
  submitting.value = true

  const results = await validate()

  // invalid, stop
  if (!results.valid) {
    submitting.value = false
    return
  }

  // traditional form submit
  if (formRef.value instanceof HTMLFormElement) {
    // wait until next tick to make sure v-model update
    await nextTick()
    formRef.value.submit()
  }
}
</script>
